// Fonte única de verdade para o conceito de "nicho" de uma empresa (tenant).
// Antes desta migração, o código comparava `company?.niche === 'acm'` (string
// solta, sem checagem de tipo) espalhado por ~35 arquivos. Qualquer typo
// (ex.: 'Acm', 'ACM') passava silenciosamente pelo TypeScript e quebrava a
// feature em runtime. Centralizar aqui dá checagem de tipo em tempo de
// compilação e um único lugar para adicionar/renomear um nicho no futuro.

export const NICHES = ['acm', 'cursos', 'clinica', 'estetica'] as const;

export type Niche = (typeof NICHES)[number];

export const NICHE_LABELS: Record<Niche, string> = {
  acm: 'Portas ACM',
  cursos: 'Cursos',
  clinica: 'Clínica',
  estetica: 'Estética',
};

function normalize(value: string | null | undefined): string | undefined {
  return value ? value.trim().toLowerCase() : undefined;
}

export function isValidNiche(value: string | null | undefined): value is Niche {
  const normalized = normalize(value);
  return !!normalized && (NICHES as readonly string[]).includes(normalized);
}

/**
 * Compara o nicho de uma empresa com um nicho conhecido, tolerando
 * diferenças de caixa/espaço (o schema não garante o valor normalizado).
 *
 * Retorna um boolean simples (não é type guard): uma função genérica
 * `company is T` parece atraente para preservar o estreitamento de tipo que
 * `company?.niche !== 'x'` dava de graça, mas o TypeScript colapsa o ramo
 * negado para `never` quando T é exatamente o tipo não-nulo do parâmetro
 * (bug conhecido de guards genéricos auto-referentes). Onde o chamador
 * precisar de `company` não-nulo depois do `if`, adicione um `!company`
 * explícito antes/junto do `isNiche(...)` — a checagem de truthiness nativa
 * do TS estreita corretamente, sem essa armadilha.
 */
export function isNiche(
  company: { niche?: string | null } | null | undefined,
  niche: Niche
): boolean {
  return normalize(company?.niche) === niche;
}

export function getNiche(
  company: { niche?: string | null } | null | undefined
): Niche | undefined {
  const normalized = normalize(company?.niche);
  return isValidNiche(normalized) ? normalized : undefined;
}
